import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import { shouldUseFoundationFallback, MOCK_LIVE_POLLS } from "@/lib/demo/executiveDemoMock";

export type PollStatus = "draft" | "scheduled" | "live" | "paused" | "completed";
export type PollRewardType =
  | "fan_points"
  | "coupon"
  | "sweepstakes"
  | "physical_prize"
  | "betting_bonus"
  | "no_reward";

export interface PollOption {
  id: string;
  poll_id: string;
  label: string;
  sort_order: number;
}

export interface Poll {
  id: string;
  tenant_id: string | null;
  title: string;
  description: string | null;
  question: string;
  status: PollStatus;
  reward_type: PollRewardType;
  reward_value: number;
  starts_at: string | null;
  ends_at: string | null;
  countries: string[];
  target_segments: string[];
  sponsor: string | null;
  created_at: string;
  updated_at: string;
}

export interface PollWithOptions extends Poll {
  options: PollOption[];
}

export interface PollResults {
  total: number;
  byOption: Record<string, number>;
}

const TBL_POLLS = "polls" as const;
const TBL_OPTS = "poll_options" as const;
const TBL_RESP = "poll_responses" as const;

/* eslint-disable @typescript-eslint/no-explicit-any */
const sb = supabase as any;

export async function listPolls(): Promise<PollWithOptions[]> {
  const { data: polls, error } = await sb.from(TBL_POLLS).select("*").order("created_at", { ascending: false });
  if (error) throw error;
  const ids = (polls ?? []).map((p: Poll) => p.id);
  if (!ids.length) return [];
  const { data: opts } = await sb.from(TBL_OPTS).select("*").in("poll_id", ids).order("sort_order");
  return (polls as Poll[]).map((p) => ({
    ...p,
    options: (opts ?? []).filter((o: PollOption) => o.poll_id === p.id),
  }));
}

export async function listLivePolls(country?: string): Promise<PollWithOptions[]> {
  const { data: polls, error } = await sb
    .from(TBL_POLLS)
    .select("*")
    .eq("status", "live")
    .order("created_at", { ascending: false });
  if (error) {
    return shouldUseFoundationFallback() ? [...MOCK_LIVE_POLLS] : [];
  }
  const filtered = (polls as Poll[]).filter((p) => {
    if (!p.countries?.length) return true;
    if (!country) return true;
    return p.countries.includes(country);
  });
  const ids = filtered.map((p) => p.id);
  if (!ids.length) {
    return shouldUseFoundationFallback() ? [...MOCK_LIVE_POLLS] : [];
  }
  const { data: opts } = await sb.from(TBL_OPTS).select("*").in("poll_id", ids).order("sort_order");
  return filtered.map((p) => ({ ...p, options: (opts ?? []).filter((o: PollOption) => o.poll_id === p.id) }));
}

export async function createPoll(input: {
  title: string;
  question: string;
  description?: string;
  reward_type: PollRewardType;
  reward_value: number;
  countries: string[];
  target_segments: string[];
  starts_at?: string | null;
  ends_at?: string | null;
  tenant_id?: string | null;
  sponsor?: string | null;
  options: string[];
  status?: PollStatus;
}): Promise<Poll | null> {
  const { data, error } = await sb
    .from(TBL_POLLS)
    .insert({
      title: input.title,
      question: input.question,
      description: input.description ?? null,
      reward_type: input.reward_type,
      reward_value: input.reward_value,
      countries: input.countries,
      target_segments: input.target_segments,
      starts_at: input.starts_at ?? null,
      ends_at: input.ends_at ?? null,
      tenant_id: input.tenant_id ?? null,
      sponsor: input.sponsor ?? null,
      status: input.status ?? "draft",
    })
    .select()
    .single();
  if (error || !data) throw error;
  const poll = data as Poll;
  if (input.options.length) {
    await sb.from(TBL_OPTS).insert(
      input.options.map((label, i) => ({ poll_id: poll.id, label, sort_order: i })),
    );
  }
  void trackEvent("poll_created", { id: poll.id, title: poll.title });
  return poll;
}

export async function updatePollStatus(id: string, status: PollStatus): Promise<void> {
  const { error } = await sb.from(TBL_POLLS).update({ status }).eq("id", id);
  if (error) throw error;
  const evt =
    status === "live"
      ? "poll_published"
      : status === "paused"
      ? "poll_paused"
      : status === "completed"
      ? "poll_completed"
      : "poll_updated";
  void trackEvent(evt, { id, status });
}

export async function deletePoll(id: string): Promise<void> {
  const { error } = await sb.from(TBL_POLLS).delete().eq("id", id);
  if (error) throw error;
  void trackEvent("poll_updated", { id, action: "delete" });
}

export async function duplicatePoll(p: PollWithOptions): Promise<Poll | null> {
  return createPoll({
    title: `${p.title} (copy)`,
    question: p.question,
    description: p.description ?? undefined,
    reward_type: p.reward_type,
    reward_value: p.reward_value,
    countries: p.countries,
    target_segments: p.target_segments,
    sponsor: p.sponsor ?? undefined,
    tenant_id: p.tenant_id ?? undefined,
    options: p.options.map((o) => o.label),
    status: "draft",
  });
}

export async function recordPollResponse(input: {
  poll_id: string;
  option_id: string;
  country?: string;
  city?: string;
  session_id?: string;
  tenant_id?: string;
}): Promise<{ ok: boolean; reward?: ResolvedReward }> {
  const { data: userData } = await supabase.auth.getUser();
  const user_id = userData.user?.id ?? null;
  const { error } = await sb.from(TBL_RESP).insert({
    poll_id: input.poll_id,
    option_id: input.option_id,
    user_id,
    session_id: input.session_id ?? null,
    country: input.country ?? null,
    city: input.city ?? null,
    tenant_id: input.tenant_id ?? null,
  });
  if (error) return { ok: false };
  void trackEvent("poll_response_recorded", { poll_id: input.poll_id, option_id: input.option_id });
  // Resolve reward
  const { data: poll } = await sb.from(TBL_POLLS).select("reward_type,reward_value").eq("id", input.poll_id).single();
  const reward = poll ? resolveReward(poll.reward_type, Number(poll.reward_value ?? 0)) : undefined;
  if (reward) void trackEvent("reward_resolved", { poll_id: input.poll_id, type: reward.type });
  return { ok: true, reward };
}

export async function getPollResults(poll_id: string): Promise<PollResults> {
  const { data } = await sb.from(TBL_RESP).select("option_id").eq("poll_id", poll_id);
  const byOption: Record<string, number> = {};
  let total = 0;
  for (const r of (data ?? []) as { option_id: string }[]) {
    byOption[r.option_id] = (byOption[r.option_id] ?? 0) + 1;
    total += 1;
  }
  return { total, byOption };
}

export interface ResolvedReward {
  type: PollRewardType;
  value: number;
  code?: string;
  qrPayload?: string;
  expiresAt?: string;
  instructions: string;
}

export function resolveReward(type: PollRewardType, value: number): ResolvedReward | undefined {
  if (type === "no_reward") return undefined;
  const code = `VS-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const instructions: Record<PollRewardType, string> = {
    fan_points: `+${value} fan points acreditados a tu cuenta.`,
    coupon: `Presenta este código en el partner. Vence ${expiresAt.slice(0, 10)}.`,
    sweepstakes: `Has sido inscrito al sorteo. Te avisaremos al ganar.`,
    physical_prize: `Premio físico — recibirás un email con instrucciones de envío.`,
    betting_bonus: `Bonus de apuesta de $${value} aplicado a tu cuenta partner.`,
    no_reward: ``,
  };
  return {
    type,
    value,
    code,
    qrPayload: `vs:reward:${code}`,
    expiresAt,
    instructions: instructions[type],
  };
}